import { useState, useEffect } from 'react'
import { api } from '../utils/api'
import { t } from '../i18n/index'
import type { ExportTask } from '../types'

export default function ExportPage() {
  const [tasks, setTasks] = useState<ExportTask[]>([])
  const [loading, setLoading] = useState(true)

  const refreshTasks = async () => {
    try {
      const res = await api.listTasks() as { tasks: ExportTask[] }
      setTasks(res.tasks || [])
    } catch (e) { console.error(e) }
    finally { setLoading(false) }
  }

  useEffect(() => { refreshTasks() }, [])

  const handleDownload = async (task: ExportTask) => {
    api.downloadExport(task.id)
    api.markDownloaded(task.id).catch(() => {})
  }

  const latestTask = tasks.find(t => t.status === 'processing' || t.status === 'completed')

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="font-mono text-xs font-bold uppercase tracking-widest text-gray-400">{t('export.title')}</div>
          <div className="font-mono text-[11px] text-gray-400 mt-0.5">{tasks.length} TOTAL TASKS</div>
        </div>
        <button onClick={refreshTasks} className="cyber-btn-outline text-xs">{t('export.refresh')}</button>
      </div>

      {latestTask && (
        <div className="cyber-card mb-5">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-2 h-2 rounded-sm ${
                latestTask.status === 'processing' ? 'bg-yellow-500 animate-pulse' :
                latestTask.status === 'completed' ? 'bg-cyber-success' : 'bg-gray-400'
              }`} />
              <span className="font-mono text-xs font-bold uppercase tracking-wider text-gray-900">
                {latestTask.status === 'processing' ? t('export.processing') : t('export.completed')}
              </span>
            </div>
            {latestTask.status === 'processing' && <span className="font-mono text-xs text-gray-500">{latestTask.progress ?? 0}%</span>}
          </div>
          {latestTask.status === 'processing' && (
            <div className="h-1 bg-gray-100"><div className="h-full bg-cyber-primary transition-all duration-500" style={{ width: `${latestTask.progress ?? 0}%` }} /></div>
          )}
          <div className="px-5 py-4">
            <div className="font-mono text-sm font-bold text-gray-900 mb-1">{latestTask.output_filename || 'UNTITLED'}</div>
            <div className="font-mono text-[11px] text-gray-400">{latestTask.format?.toUpperCase()} · {latestTask.resolution}</div>
            {latestTask.status === 'completed' && latestTask.output_path && (
              <div className="mt-4 flex gap-3">
                <button onClick={() => handleDownload(latestTask)} className="cyber-btn-primary">{t('export.download')}</button>
                <button onClick={() => { handleDownload(latestTask); alert(t('tips.usb')) }} className="cyber-btn-outline">{t('export.save-usb')}</button>
                <button onClick={() => { handleDownload(latestTask); alert(t('tips.bt')) }} className="cyber-btn-outline">{t('export.bluetooth')}</button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="cyber-card">
        <div className="px-5 py-3 border-b border-gray-100">
          <span className="font-mono text-xs font-bold uppercase tracking-widest text-gray-500">HISTORY</span>
        </div>
        {loading ? (
          <div className="p-8 text-center font-mono text-xs text-gray-400 uppercase tracking-widest">LOADING...</div>
        ) : tasks.length === 0 ? (
          <div className="p-8 text-center font-mono text-xs text-gray-400 uppercase tracking-widest">{t('export.no-tasks')}</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {tasks.map((task) => (
              <div key={task.id} className="flex items-center gap-4 px-5 py-3.5 hover:bg-gray-50 transition-colors">
                <div className={`w-1.5 h-1.5 rounded-sm flex-shrink-0 ${
                  task.status === 'completed' ? 'bg-cyber-success' :
                  task.status === 'processing' ? 'bg-yellow-500 animate-pulse' :
                  task.status === 'failed' ? 'bg-red-500' : 'bg-gray-300'
                }`} />
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-xs font-bold text-gray-900 truncate">{task.output_filename || 'UNTITLED'}</div>
                  <div className="font-mono text-[10px] text-gray-400 mt-0.5">{task.format?.toUpperCase()} · {task.resolution} · {task.status.toUpperCase()}</div>
                </div>
                {task.status === 'completed' && task.output_path && (
                  <button onClick={() => handleDownload(task)} className="font-mono text-[10px] font-bold text-cyber-primary hover:text-teal-600 uppercase tracking-wider transition-colors flex-shrink-0">[DL]</button>
                )}
                {task.status === 'pending' && (
                  <button onClick={() => api.cancelTask(task.id)} className="font-mono text-[10px] font-bold text-gray-400 hover:text-red-500 uppercase tracking-wider transition-colors flex-shrink-0">[X]</button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
